import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL, API_ENDPOINTS, API_TIMEOUT, DEFAULT_HEADERS, SECONDARY_API_BASE_URL, getApiUrl } from '@/config/api'
import { fileNameFromContentDisposition, saveOrShareBlob } from '@/utils/fileDownload'
import { expireSession } from '@/utils/sessionExpiry'
import { invalidateCachedData } from '@/utils/dataCache'

// Extended interface for custom config options
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  bypassRedirect?: boolean
  _retry?: boolean
  _fallbackTried?: boolean
}

// Whether an automatic fallback base URL is configured and actually
// different from the primary - guards against retrying against the same
// host when no real secondary is set up.
const hasApiFallback = Boolean(SECONDARY_API_BASE_URL) && SECONDARY_API_BASE_URL !== API_BASE_URL

const AUTH_CREDENTIAL_URL = /\/auth\/(login|register|otp|token\/refresh|refresh-token|quick-login|invitations)/i
let silentRefresh: Promise<{ accessToken: string | null; sessionInvalid: boolean }> | null = null

const readStoredAuth = () => {
  const authData = localStorage.getItem('mastermind-auth')
  if (!authData) return { accessToken: null as string | null, refreshToken: null as string | null }
  try {
    const parsedAuth = JSON.parse(authData)
    return {
      accessToken: parsedAuth.accessToken || parsedAuth.state?.accessToken || null,
      refreshToken: parsedAuth.refreshToken || parsedAuth.state?.refreshToken || null
    }
  } catch (error) {
    console.error('Error parsing auth data:', error)
    return { accessToken: null as string | null, refreshToken: null as string | null }
  }
}

const isAuthCredentialRequest = (config?: AxiosRequestConfig) => {
  const url = `${config?.baseURL || ''}${config?.url || ''}`
  return AUTH_CREDENTIAL_URL.test(url)
}

const applyAccessToken = (config: AxiosRequestConfig, token: string) => {
  config.headers = config.headers || {}
  const headers = config.headers as { set?: (name: string, value: string) => void; Authorization?: string }
  if (typeof headers.set === 'function') {
    headers.set('Authorization', `Bearer ${token}`)
  } else {
    headers.Authorization = `Bearer ${token}`
  }
}

const persistRefreshedSession = async (accessToken: string, refreshToken: string, user?: unknown) => {
  try {
    const raw = localStorage.getItem('mastermind-auth')
    const parsed = raw ? JSON.parse(raw) : {}
    parsed.accessToken = accessToken
    parsed.refreshToken = refreshToken
    if (user) parsed.user = user
    localStorage.setItem('mastermind-auth', JSON.stringify(parsed))
  } catch {
    // Pinia persistence is updated below when the store is available.
  }

  const { useAuthStore } = await import('@/stores/auth')
  const authStore = useAuthStore()
  authStore.setTokens(accessToken, refreshToken)
  if (user) authStore.setUser(user)
}

const refreshAccessTokenSilently = async (): Promise<{ accessToken: string | null; sessionInvalid: boolean }> => {
  if (silentRefresh) return silentRefresh

  silentRefresh = (async () => {
    const { refreshToken } = readStoredAuth()
    if (!refreshToken) return { accessToken: null, sessionInvalid: true }
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`,
        { refreshToken },
        { headers: DEFAULT_HEADERS, timeout: 0, withCredentials: true }
      )
      const payload = response.data?.data || response.data || {}
      const accessToken = payload.accessToken || payload.AccessToken
      const nextRefreshToken = payload.refreshToken || payload.RefreshToken
      if (!accessToken || !nextRefreshToken) return { accessToken: null, sessionInvalid: true }
      await persistRefreshedSession(accessToken, nextRefreshToken, payload.user || payload.User)
      return { accessToken: accessToken as string, sessionInvalid: false }
    } catch (error: any) {
      const status = error?.response?.status
      return { accessToken: null, sessionInvalid: status === 401 || status === 400 }
    } finally {
      silentRefresh = null
    }
  })()

  return silentRefresh
}

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: DEFAULT_HEADERS,
  withCredentials: true,
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = readStoredAuth()
    if (accessToken && !isAuthCredentialRequest(config)) {
      applyAccessToken(config, accessToken)
    }
    
    // Add sessionId to params only for GET requests (for filtering)
    // POST/PUT/DELETE operations should use the backend's active session logic
    const sessionEndpoints = ['/sessions', '/auth']
    const isSessionEndpoint = sessionEndpoints.some(endpoint => config.url?.startsWith(endpoint))
    const isWriteOperation = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')
    
    if (!isSessionEndpoint && !isWriteOperation) {
      const selectedSessionId = localStorage.getItem('selectedSessionId')
      if (selectedSessionId) {
        // Add sessionId to query params only for GET requests
        config.params = {
          ...config.params,
          sessionId: parseInt(selectedSessionId, 10)
        }
      }
    }
    
    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`)
    }
    
    return config
  },
  (error) => {
    console.error('Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor to handle common responses
apiClient.interceptors.response.use(
  (response) => {
    const method = response.config.method?.toLowerCase()
    if (method && method !== 'get' && method !== 'head') {
      invalidateCachedData(['admin-dashboard', 'parent', 'teacher', 'notifications', 'attendance', 'finance', 'students'])
    }

    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`)
    }
    
    return response
  },
  async (error) => {
    // Handle common errors
    if (error.response) {
      const { status, data } = error.response
      const original = error.config as ExtendedAxiosRequestConfig | undefined

      if (status === 401 && original && !isAuthCredentialRequest(original) && !original.bypassRedirect) {
        if (!original._retry) {
          original._retry = true
          const refreshed = await refreshAccessTokenSilently()
          if (refreshed.accessToken) {
            applyAccessToken(original, refreshed.accessToken)
            return apiClient.request(original)
          }
          if (!refreshed.sessionInvalid) {
            return Promise.reject(error)
          }
        }
        expireSession()
      }

      switch (status) {
        case 403:
          // Forbidden - show access denied message
          console.error('Access denied: You do not have permission to access this resource')
          break
          
        case 404:
          // Not found
          console.error('Resource not found')
          break
          
        case 500:
          // Server error
          console.error('Server error. Please try again later.')
          break
      }
      
      if (import.meta.env.DEV) {
        console.error(`❌ API Error: ${status} ${error.config?.url}`, data)
      }
    } else if (error.request) {
      // Network error - no response received (timeout, DNS failure, quota
      // outage, etc). If a fallback API is configured, retry this exact
      // request against it once before giving up.
      const original = error.config as ExtendedAxiosRequestConfig | undefined
      if (hasApiFallback && original && !original._fallbackTried) {
        original._fallbackTried = true
        original.baseURL = SECONDARY_API_BASE_URL
        if (import.meta.env.DEV) {
          console.warn(`⚠️ Primary API unreachable, retrying via fallback: ${SECONDARY_API_BASE_URL}`)
        }
        return apiClient.request(original)
      }

      console.error('Network error. Please check your connection.')
      if (import.meta.env.DEV) {
        console.error('Network Error:', error.request)
      }
    } else {
      // Other error
      console.error('Request setup error:', error.message)
    }
    
    return Promise.reject(error)
  }
)

// Generic API methods
export const apiService = {
  // GET request
  async get<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.get<T>(endpoint, config)
    return response.data
  },

  // POST request
  async post<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.post<T>(endpoint, data, config)
    return response.data
  },

  // PUT request
  async put<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.put<T>(endpoint, data, config)
    return response.data
  },

  // PATCH request
  async patch<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.patch<T>(endpoint, data, config)
    return response.data
  },

  // DELETE request
  async delete<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.delete<T>(endpoint, config)
    return response.data
  },

  // Upload file
  async upload<T = any>(endpoint: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    }

    const response = await apiClient.post<T>(endpoint, formData, config)
    return response.data
  },

  // Download file
  async download(endpoint: string, filename?: string): Promise<void> {
    const response = await apiClient.get(endpoint, {
      responseType: 'blob',
    })
    const resolvedFileName = filename
      || fileNameFromContentDisposition(response.headers['content-disposition'], 'download')
    const contentType = response.headers['content-type'] || 'application/octet-stream'
    await saveOrShareBlob(new Blob([response.data], { type: contentType }), resolvedFileName)
  },

  async getBlob(endpoint: string): Promise<Blob> {
    const response = await apiClient.get(endpoint, { responseType: 'blob' })
    const contentType = response.headers['content-type'] || 'application/octet-stream'
    return response.data instanceof Blob ? response.data : new Blob([response.data], { type: contentType })
  },

  // GET request without automatic redirect on 401
  async getWithoutRedirect(endpoint: string, params?: any): Promise<any> {
    const config: ExtendedAxiosRequestConfig = {
      params,
      bypassRedirect: true // Custom flag to bypass redirect
    }
    const response = await apiClient.get(endpoint, config)
    return response.data
  },
}

// Export the axios instance for custom requests
export { apiClient }

// Export helper function to get full API URL
export { getApiUrl }

// Types for API responses
export interface ApiResponse<T = any> {
  success: boolean
  data: T
  message?: string
  errors?: Record<string, string[]>
}

export interface PaginatedResponse<T = any> {
  success: boolean
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  message?: string
}
